import { useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { inputClass } from './fields';
import type { ActionResult } from '../lib/members';

const PHRASE = 'ERASE';

/**
 * Spec 004 — the erasure confirmation (SC-419/420). A deliberate, slow gate: it states plainly what
 * erasure does and does not undo, then requires the person to type ERASE before the button enables. The
 * weight comes from the copy and the typed gate, not a wall of red — the destructive button is bordered,
 * not filled. Reused by the member's own Privacy page and the admin's on-behalf action.
 */
export function ConfirmErase({
  subject,
  onErase,
  onDone,
}: {
  subject: string; // e.g. "your account" or a member's display name
  onErase: (confirm: string) => Promise<ActionResult>;
  onDone: () => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = value === PHRASE;

  async function run() {
    if (!matches || busy) return;
    setBusy(true);
    setError(null);
    const r = await onErase(value);
    setBusy(false);
    if (r.kind === 'ok') {
      onDone();
      return;
    }
    if (r.kind === 'conflict') setError(r.message); // e.g. last_admin
    else if (r.kind === 'invalid') setError(r.message ?? 'That confirmation didn’t match.');
    else if (r.kind === 'forbidden') setError('You don’t have permission to do that.');
    else if (r.kind === 'not_found') setError(r.message);
    else setError('We couldn’t complete the erasure. Please try again.');
  }

  return (
    <div className="rounded-md border border-error/40 bg-error/5 p-5">
      <p className="flex items-start gap-2 font-display text-sm font-semibold uppercase tracking-[0.1em] text-error">
        <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
        This cannot be undone
      </p>
      <p className="mt-3 font-serif text-sm leading-relaxed text-ink">
        Erasing {subject} permanently removes the profile and personal details, and the account can no
        longer sign in. Records of governance decisions are kept for the association’s history, but they
        will no longer identify {subject === 'your account' ? 'you' : 'them'}.
      </p>

      {error && (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-md border border-error/50 bg-bg px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      )}

      <label htmlFor="erase-confirm" className="mt-4 block font-sans text-sm font-medium text-ink">
        Type <span className="font-semibold tracking-widest">{PHRASE}</span> to confirm
      </label>
      <input
        id="erase-confirm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="off"
        aria-describedby="erase-help"
        className={`${inputClass} mt-1.5 max-w-xs`}
      />
      <p id="erase-help" className="mt-1 font-sans text-xs text-muted">The button stays disabled until it matches exactly.</p>

      <button
        type="button"
        onClick={run}
        disabled={!matches || busy}
        className="mt-4 inline-flex items-center gap-2 rounded-md border border-error px-4 py-2.5 font-sans text-sm font-semibold text-error transition-colors duration-150 ease-standard hover:bg-error hover:text-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-error"
      >
        {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
        Erase {subject === 'your account' ? 'my account' : 'this member'}
      </button>
    </div>
  );
}
